import { useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { CheckCircle, Loader2, AlertTriangle } from "lucide-react";
import { useLanguage } from "@/contexts/language";
import bgDayUrl from "../assets/bg-day.png";
import bgNightUrl from "../assets/bg-night.png";
import logoUrl from "../assets/logo.png";

const BASE_URL = import.meta.env.BASE_URL?.replace(/\/$/, "") ?? "";

function isDaytime(): boolean {
  const now = new Date();
  const month = now.getMonth();
  const localTime = now.getHours() + now.getMinutes() / 60;
  const civilTwilight: [number, number][] = [
    [7.5,  17.0],
    [7.0,  17.75],
    [6.25, 18.5],
    [6.0,  20.0],
    [5.5,  20.75],
    [5.0,  21.25],
    [5.25, 21.0],
    [5.75, 20.25],
    [6.5,  19.25],
    [7.0,  18.0],
    [7.5,  17.0],
    [7.75, 16.75],
  ];
  const [dawn, dusk] = civilTwilight[month];
  return localTime >= dawn && localTime < dusk;
}

const inputCls =
  "w-full px-4 py-2.5 rounded-lg border border-white/20 bg-white/10 text-white " +
  "placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-white/30 " +
  "focus:border-white/50 transition-all text-sm font-mono backdrop-blur-sm";

const labelCls = "block text-xs font-semibold text-white/60 uppercase tracking-wider mb-1.5";

export default function ResetPasswordPage() {
  const [, navigate] = useLocation();
  const { t } = useLanguage();
  const rp = t.resetPassword;
  const token = new URLSearchParams(window.location.search).get("token") ?? "";

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const day = isDaytime();
  const bgImage = day ? bgDayUrl : bgNightUrl;

  useEffect(() => {
    if (!token) setError(rp.missingToken);
  }, [token, rp.missingToken]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setError(rp.passwordMismatch);
      return;
    }
    setError("");
    setLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/api/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, newPassword }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? rp.resetFailed);
      setDone(true);
      setTimeout(() => navigate("/"), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : rp.resetFailed);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 relative"
      style={{ backgroundImage: `url(${bgImage})`, backgroundSize: "cover", backgroundPosition: "center" }}
    >
      <div className="absolute inset-0 bg-black/45" />

      <div className="relative z-10 w-full max-w-md flex flex-col items-center gap-4">
        <div className="flex flex-col items-center gap-3 px-4">
          <img src={logoUrl} alt="Flight Deck" className="w-full max-w-[36rem] drop-shadow-2xl" />
        </div>

        <div className="w-full bg-black/40 backdrop-blur-md rounded-2xl border border-white/15 shadow-2xl p-8">
          <div className="text-center mb-6">
            <h1 className="text-white font-mono text-lg font-bold tracking-wider">Reset Your Password</h1>
            <p className="text-white/50 font-mono text-xs mt-1.5">Enter your new password below</p>
          </div>

          {!token && (
            <div className="text-center space-y-4">
              <AlertTriangle className="w-10 h-10 text-red-400/70 mx-auto" />
              <p className="text-red-300 font-mono text-xs">{rp.invalidLink}</p>
              <Link href="/" className="block text-white/50 font-mono text-xs hover:text-white transition-colors underline">
                {rp.backToLogin}
              </Link>
            </div>
          )}

          {token && !done && (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className={labelCls}>{rp.newPasswordLabel}</label>
                <input
                  type="password" required
                  value={newPassword} onChange={e => setNewPassword(e.target.value)}
                  className={`${inputCls} tracking-widest`} placeholder="••••••••"
                  autoFocus autoComplete="new-password"
                />
              </div>
              <div>
                <label className={labelCls}>{rp.confirmPasswordLabel}</label>
                <input
                  type="password" required
                  value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
                  className={`${inputCls} tracking-widest`} placeholder="••••••••"
                  autoComplete="new-password"
                />
              </div>
              <p className="text-white/25 font-mono text-[10px] leading-relaxed">{rp.hint}</p>
              {error && (
                <div className="text-red-300 text-xs border border-red-400/30 rounded-lg px-3 py-2.5 bg-red-500/15 font-mono">
                  {error}
                </div>
              )}
              <button
                type="submit"
                disabled={loading || !newPassword || !confirmPassword}
                className="w-full bg-blue-600 text-white rounded-lg font-bold py-2.5 hover:bg-blue-500 disabled:opacity-40 disabled:cursor-not-allowed transition-all text-sm font-mono tracking-wider mt-1"
              >
                {loading
                  ? <><Loader2 className="inline w-4 h-4 mr-2 animate-spin" />{rp.saving}</>
                  : "Update Password"}
              </button>
            </form>
          )}

          {done && (
            <div className="text-center space-y-4">
              <CheckCircle className="w-12 h-12 text-white/70 mx-auto" />
              <p className="text-white font-mono text-sm font-bold tracking-wider">{rp.successTitle}</p>
              <p className="text-white/40 font-mono text-xs">{rp.successBody}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
